import {
  GetAllBones,
  getRgObjectsOfType,
  RG_TYPE,
  type Building,
  type V3,
} from "@project-rouge/rg-core";
import { get2dPlanObjs, get2dRgObjectValues } from "@project-rouge/rg-three";
import { useMemo } from "react";
import { Matrix4, Vector3, type Matrix4Tuple } from "three";
import type { StageSnapshot } from "src/screens/tessellation-steps-data";

// Custom 2D plan SVG for the tessellation-steps screen. SvgPlan only draws
// size-rectangles, but the early pipeline stages (subdivision, skeleton) are
// polygon and line data that SvgPlan can't show. This component reuses
// get2dPlanObjs + get2dRgObjectValues for rect-shaped objects and adds:
//   - polygons for Site / Plot / EmptySpace (transformed by their world matrix)
//   - skeleton-tailor threads (from meta.mainThreads + mainFrozenThreads)
//   - muscle lines (walked from building.data.anatomy)
//
// Coordinate convention matches SvgPlan: svg-x = world-x, svg-y = world-z.

type RectStyle = {
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  fillOpacity?: number;
};

// Site is intentionally excluded — its default data.polygon is a placeholder
// shape (unrelated to the zone the user drew) and just adds noise.
const POLYGON_TYPES: ReadonlySet<RG_TYPE> = new Set([
  RG_TYPE.Plot,
  RG_TYPE.EmptySpace,
]);

const RECT_TYPES: ReadonlySet<RG_TYPE> = new Set([
  RG_TYPE.Module,
  RG_TYPE.Core,
  RG_TYPE.Corridor,
  RG_TYPE.Entrance,
  RG_TYPE.TechnicalSpace,
  RG_TYPE.Facade,
]);

const VISIBLE: RG_TYPE[] = [...POLYGON_TYPES, ...RECT_TYPES];

const STYLES: Partial<Record<RG_TYPE, RectStyle>> = {
  [RG_TYPE.Plot]: { fill: "#f3f3f3", stroke: "#888", strokeWidth: 0.05 },
  [RG_TYPE.EmptySpace]: { fill: "transparent", stroke: "#bbb", strokeWidth: 0.03 },
  [RG_TYPE.Module]: { fill: "#a7dbd8", stroke: "#3a3a3a", strokeWidth: 0.04, fillOpacity: 0.7 },
  [RG_TYPE.Core]: { fill: "#555", stroke: "#222", strokeWidth: 0.04 },
  [RG_TYPE.Corridor]: { fill: "#dddddd", stroke: "#888", strokeWidth: 0.03 },
  [RG_TYPE.Entrance]: { fill: "#ffd87d", stroke: "#444", strokeWidth: 0.04 },
  [RG_TYPE.TechnicalSpace]: { fill: "#bdbdbd", stroke: "#444", strokeWidth: 0.04 },
  [RG_TYPE.Facade]: { fill: "none", stroke: "#222", strokeWidth: 0.06 },
};

const _v = new Vector3();
const _m = new Matrix4();

function transformPolygon(points: readonly Readonly<V3>[], mx: Matrix4Tuple) {
  _m.fromArray(mx);
  return points.map((p) => {
    _v.set(p[0], p[1], p[2]).applyMatrix4(_m);
    return { x: _v.x, z: _v.z };
  });
}

export function TessellationStageSvg({ snapshot }: { snapshot: StageSnapshot }) {
  const data = useMemo(() => buildSceneData(snapshot), [snapshot]);
  return (
    <svg
      viewBox={data.viewBox}
      preserveAspectRatio="xMidYMid meet"
      width="100%"
      height="100%"
      style={{ display: "block", background: "white" }}
    >
      {data.polygons.map((p, i) => (
        <polygon
          key={`poly${i}`}
          points={p.points.map((pt) => `${pt.x},${pt.z}`).join(" ")}
          {...STYLES[p.type]}
        />
      ))}
      {data.rects.map((r, i) => {
        const { pos, size, anchor, rotation } = r.vals;
        const style = STYLES[r.type];
        return (
          <g key={`rect${i}`} transform={`translate(${pos.join(",")})`}>
            <g
              transform={[
                `translate(${anchor.join(",")})`,
                `rotate(${rotation.join(" ")})`,
              ].join(" ")}
            >
              <rect width={size[0]} height={size[1]} {...style} />
            </g>
          </g>
        );
      })}
      {data.muscles.map((l, i) => (
        <line
          key={`m${i}`}
          x1={l.x1}
          y1={l.z1}
          x2={l.x2}
          y2={l.z2}
          stroke="#0066cc"
          strokeWidth={0.12}
          strokeLinecap="round"
        />
      ))}
      {data.threads.map((l, i) => (
        <line
          key={`t${i}`}
          x1={l.x1}
          y1={l.z1}
          x2={l.x2}
          y2={l.z2}
          stroke="#cc3333"
          strokeWidth={0.16}
          strokeLinecap="round"
        />
      ))}
    </svg>
  );
}

type SceneData = {
  polygons: { type: RG_TYPE; points: { x: number; z: number }[] }[];
  rects: { type: RG_TYPE; vals: ReturnType<typeof get2dRgObjectValues> }[];
  threads: { x1: number; z1: number; x2: number; z2: number }[];
  muscles: { x1: number; z1: number; x2: number; z2: number }[];
  viewBox: string;
};

function buildSceneData(snapshot: StageSnapshot): SceneData {
  const obj = snapshot.obj;
  const visibleArr = get2dPlanObjs({ obj, visible: VISIBLE });

  const polygons: SceneData["polygons"] = [];
  const rects: SceneData["rects"] = [];

  for (let i = 0; i < VISIBLE.length; i++) {
    const type = VISIBLE[i];
    const items = visibleArr[i] ?? [];
    for (const item of items) {
      if (POLYGON_TYPES.has(type)) {
        const poly = (item.obj.data as { polygon?: readonly Readonly<V3>[] } | undefined)
          ?.polygon;
        if (!poly || poly.length < 3) continue;
        const points = transformPolygon(poly, item.mx);
        if (!points.every((p) => Number.isFinite(p.x) && Number.isFinite(p.z))) continue;
        polygons.push({ type, points });
      } else {
        // Some objects (e.g. Facade in early stages, anything with a null
        // size axis) yield NaN through get2dRgObjectValues — skip them.
        const o = item.obj;
        if (
          !Number.isFinite(o.size[0]) ||
          !Number.isFinite(o.size[2]) ||
          !Number.isFinite(o.anchor[0]) ||
          !Number.isFinite(o.anchor[2])
        ) continue;
        const vals = get2dRgObjectValues({ obj: o, mx: item.mx });
        if (
          !Number.isFinite(vals.pos[0]) ||
          !Number.isFinite(vals.pos[1]) ||
          !Number.isFinite(vals.size[0]) ||
          !Number.isFinite(vals.size[1]) ||
          !Number.isFinite(vals.anchor[0]) ||
          !Number.isFinite(vals.anchor[1]) ||
          !Number.isFinite(vals.angle)
        ) continue;
        rects.push({ type, vals });
      }
    }
  }

  const finiteLine = (x1: number, z1: number, x2: number, z2: number) =>
    Number.isFinite(x1) && Number.isFinite(z1) && Number.isFinite(x2) && Number.isFinite(z2);

  const threads: SceneData["threads"] = [];
  const pushThread = (start: Vector3, end: Vector3) => {
    if (finiteLine(start.x, start.z, end.x, end.z)) {
      threads.push({ x1: start.x, z1: start.z, x2: end.x, z2: end.z });
    }
  };
  for (const t of snapshot.meta.mainThreads ?? []) pushThread(t.start, t.end);
  for (const t of snapshot.meta.mainFrozenThreads ?? []) pushThread(t.start, t.end);

  const muscles: SceneData["muscles"] = [];
  for (const building of getRgObjectsOfType<Building>(obj, RG_TYPE.Building)) {
    const anatomy = building.data?.anatomy;
    if (!anatomy) continue;
    for (const bone of GetAllBones(anatomy)) {
      for (const muscle of bone.muscles ?? []) {
        const [p1, p2] = muscle.points;
        if (finiteLine(p1[0], p1[2], p2[0], p2[2])) {
          muscles.push({ x1: p1[0], z1: p1[2], x2: p2[0], z2: p2[2] });
        }
      }
    }
  }

  // viewBox bounds
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  const include = (x: number, z: number) => {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (z < minZ) minZ = z;
    if (z > maxZ) maxZ = z;
  };
  for (const p of polygons) for (const pt of p.points) include(pt.x, pt.z);
  for (const r of rects) {
    const cx = r.vals.pos[0];
    const cz = r.vals.pos[1];
    const sx = r.vals.size[0];
    const sz = r.vals.size[1];
    // generous bound — diagonal of rect — accounting for rotation
    const diag = Math.hypot(sx, sz);
    include(cx - diag, cz - diag);
    include(cx + diag, cz + diag);
  }
  for (const l of [...threads, ...muscles]) {
    include(l.x1, l.z1);
    include(l.x2, l.z2);
  }
  if (!Number.isFinite(minX)) {
    minX = 0;
    maxX = 1;
    minZ = 0;
    maxZ = 1;
  }
  const span = Math.max(maxX - minX, maxZ - minZ, 1);
  const pad = span * 0.05;
  minX -= pad;
  maxX += pad;
  minZ -= pad;
  maxZ += pad;

  return {
    polygons,
    rects,
    threads,
    muscles,
    viewBox: `${minX} ${minZ} ${maxX - minX} ${maxZ - minZ}`,
  };
}
